import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";
import type {
  ListResponse,
  MarkCalibrationOutlierInput,
  NutrientCalibration,
} from "./types";

const base = "/v1/nutrient-calibrations";

export interface ListCalibrationsParams {
  nutrient_id?: string;
  since?: string;
  limit?: number;
  offset?: number;
}

export function useCalibrations(params: ListCalibrationsParams = {}) {
  return useQuery({
    queryKey: queryKeys.calibrations.list(
      params as Record<string, unknown>,
    ),
    queryFn: ({ signal }) =>
      api.get<ListResponse<NutrientCalibration>>(
        base,
        {
          nutrient_id: params.nutrient_id,
          since: params.since,
          limit: params.limit,
          offset: params.offset,
        },
        signal,
      ),
  });
}

export function useCalibration(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? queryKeys.calibrations.detail(id)
      : queryKeys.calibrations.all,
    queryFn: ({ signal }) =>
      api.get<NutrientCalibration>(`${base}/${id}`, undefined, signal),
    enabled: !!id,
  });
}

export function useMarkCalibrationOutlier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MarkCalibrationOutlierInput) =>
      api.post<void>(`${base}/${id}:mark-outlier`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calibrations.all });
    },
  });
}

export function useVoidCalibration(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | undefined) =>
      api.delete<void>(`${base}/${id}`, { reason: reason ?? "" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calibrations.all });
    },
  });
}
